import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/admin`;

// Create axios instance with auth interceptor
const adminClient = axios.create({
  baseURL: API,
});

adminClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth
export const adminAuthApi = {
  login: async (email, password) => {
    const response = await axios.post(`${API}/login`, { email, password });
    return response.data;
  },
  
  getMe: async () => {
    const response = await adminClient.get('/me');
    return response.data;
  }
};

// Dashboard
export const adminDashboardApi = {
  getStats: async () => {
    const response = await adminClient.get('/stats');
    return response.data;
  }
};

// Users
export const adminUsersApi = {
  getAll: async (params = {}) => {
    const response = await adminClient.get('/users', { params });
    return response.data;
  },
  
  getById: async (id) => {
    const response = await adminClient.get(`/users/${id}`);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await adminClient.put(`/users/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await adminClient.delete(`/users/${id}`);
    return response.data;
  }
};

// Places
export const adminPlacesApi = {
  getAll: async (params = {}) => {
    const response = await adminClient.get('/places', { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await adminClient.post('/places', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await adminClient.put(`/places/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await adminClient.delete(`/places/${id}`);
    return response.data;
  },
  
  toggleFeatured: async (id) => {
    const response = await adminClient.put(`/places/${id}/toggle-featured`);
    return response.data;
  }
};

// Routes
export const adminRoutesApi = {
  getAll: async (params = {}) => {
    const response = await adminClient.get('/routes', { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await adminClient.post('/routes', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await adminClient.put(`/routes/${id}`, data);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await adminClient.delete(`/routes/${id}`);
    return response.data;
  }
};

// Businesses
export const adminBusinessesApi = {
  getAll: async (params = {}) => {
    const response = await adminClient.get('/businesses', { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await adminClient.post('/businesses', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await adminClient.put(`/businesses/${id}`, data);
    return response.data;
  },
  
  approve: async (id) => {
    const response = await adminClient.put(`/businesses/${id}/approve`);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await adminClient.delete(`/businesses/${id}`);
    return response.data;
  },

  assignOwner: async (businessId, userId) => {
    const response = await adminClient.put(`/businesses/${businessId}/assign-owner?user_id=${userId}`);
    return response.data;
  },

  removeOwner: async (businessId) => {
    const response = await adminClient.delete(`/businesses/${businessId}/remove-owner`);
    return response.data;
  }
};

// Business Claims
export const adminBusinessClaimsApi = {
  getAll: async (params = {}) => {
    const response = await adminClient.get('/business-claims', { params });
    return response.data;
  },

  approve: async (claimId) => {
    const response = await adminClient.put(`/business-claims/${claimId}/approve`);
    return response.data;
  },

  reject: async (claimId, adminNote = '') => {
    const response = await adminClient.put(`/business-claims/${claimId}/reject?admin_note=${encodeURIComponent(adminNote)}`);
    return response.data;
  }
};

// Campaigns
export const adminCampaignsApi = {
  getAll: async (params = {}) => {
    const response = await adminClient.get('/campaigns', { params });
    return response.data;
  },
  
  create: async (data) => {
    const response = await adminClient.post('/campaigns', data);
    return response.data;
  },
  
  update: async (id, data) => {
    const response = await adminClient.put(`/campaigns/${id}`, data);
    return response.data;
  },
  
  toggleStatus: async (id) => {
    const response = await adminClient.put(`/campaigns/${id}/toggle-status`);
    return response.data;
  },
  
  delete: async (id) => {
    const response = await adminClient.delete(`/campaigns/${id}`);
    return response.data;
  }
};

// Posts (Moderation)
export const adminPostsApi = {
  getAll: async (params = {}) => {
    const response = await adminClient.get('/posts', { params });
    return response.data;
  },
  
  delete: async (id) => {
    const response = await adminClient.delete(`/posts/${id}`);
    return response.data;
  }
};

// Categories
export const adminCategoriesApi = {
  getAll: async () => {
    const response = await adminClient.get('/categories');
    return response.data;
  }
};
